import React, { useMemo } from 'react';

import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import { useController } from 'react-hook-form';

import { useFormContext } from '../FormProvider';

type InfoPart = {
  title: string;
  value: string;
};

interface Props {
  name: string;
  label: string;
  addLabel?: string;
  disabled?: boolean;
}

export const ControlledInfoPartsField: React.FC<Props> = ({ name, label, addLabel = 'Добавить', disabled = false }) => {
  const { control, errors } = useFormContext();
  const { field } = useController({ name, control });

  const items = useMemo(() => (field.value as InfoPart[] | undefined) ?? [], [field.value]);

  const onChangeTitle = (index: number, value: string) => {
    const next = [...items];
    next[index] = { ...next[index], title: value };
    field.onChange(next);
  };

  const onChangeValue = (index: number, value: string) => {
    const next = [...items];
    next[index] = { ...next[index], value };
    field.onChange(next);
  };

  const onAdd = () => {
    field.onChange([...items, { title: '', value: '' }]);
  };

  const onRemove = (index: number) => {
    field.onChange(items.filter((_, i) => i !== index));
  };

  const error = errors?.[name] as { message?: string } | undefined;

  return (
    <Stack spacing={1.5} sx={{ mt: 1 }}>
      <Typography variant="subtitle2">{label}</Typography>
      {items.map((item, index) => (
        <Box
          key={`${name}-${index}`}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1.5,
            backgroundColor: 'background.paper',
          }}
        >
          <Stack spacing={1}>
            <TextField
              label="Заголовок"
              value={item.title}
              onChange={(event) => onChangeTitle(index, event.target.value)}
              disabled={disabled}
              fullWidth
            />
            <TextField
              label="Описание"
              value={item.value}
              onChange={(event) => onChangeValue(index, event.target.value)}
              disabled={disabled}
              fullWidth
              multiline
              rows={3}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton onClick={() => onRemove(index)} disabled={disabled} color="error" size="small">
                <DeleteOutlineRoundedIcon />
              </IconButton>
            </Box>
          </Stack>
        </Box>
      ))}
      <Button variant="outlined" onClick={onAdd} disabled={disabled}>
        {addLabel}
      </Button>
      {error?.message && (
        <Typography variant="caption" color="error">
          {error.message}
        </Typography>
      )}
    </Stack>
  );
};
